import { flashError, flashSuccess } from "../lib/flash";
import ApplicationController from "../turbo/application_controller";

/**
 * A controller used to copy text to the clipboard.
 *
 * This controller and its actions all target a single element, and this element
 * must provide a "data-copy-text-value" which is the text that will be copied
 * to the clipboard.
 *
 * Optionally, the element can also provide an inner  "buttonText" target. If
 * specified, this target will briefly have its text updated to "Copied". Also,
 * if a `data-flash-success-value` is included, this value will be used to
 * populate a short flash message after copy.
 *
 * This is similar to the turbo/copy_controller but has a much shorter scope of
 * use.
 *
 * Example:
 *
 *   <span
 *      data-controller="clipboard"
 *      data-clipboard-copy-text-value="Hello!"
 *      data-action="click->clickboard#copyText">Click me to copy!</span>
 *
 */
export default class extends ApplicationController {
    static values = {
        copyText: String,
        flashSuccess: String
    };

    static targets = ["buttonText"];

    copyText() {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(this.copyTextValue);
            this.onCopySuccess();
        } else {
            this.onCopyFailure();
        }
    }

    onCopySuccess() {
        if (this.hasButtonTextTarget) {
            const originalText = this.buttonTextTarget.innerText;
            this.buttonTextTarget.innerText = "Copied";

            setTimeout(() => {
                this.buttonTextTarget.innerText = originalText;
            }, 3000);
        }

        if (this.hasFlashSuccessValue) {
            flashSuccess(this.flashSuccessValue);
        }
    }

    onCopyFailure() {
        flashError("Unable to copy text");
    }
}
